import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["copyButton", "code"]

  connect() {
    this.setupCopyButtons()
  }

  setupCopyButtons() {
    // Find all copy buttons in the documentation
    const copyButtons = this.element.querySelectorAll('.copy-button')
    
    copyButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        this.copyCode(button)
      })
    })
  }

  async copyCode(button) {
    try {
      // Find the code block associated with this button
      const codeBlock = button.closest('.relative').querySelector('pre code')
      
      if (!codeBlock) {
        console.error('Code block not found')
        return
      }

      const textToCopy = codeBlock.textContent || codeBlock.innerText
      
      // Use the modern clipboard API if available
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy)
      } else {
        // Fallback for older browsers
        this.fallbackCopyText(textToCopy)
      }

      // Visual feedback
      this.showCopyFeedback(button)
      
    } catch (error) {
      console.error('Failed to copy code:', error)
      this.showCopyError(button)
    }
  }

  fallbackCopyText(text) {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    
    try {
      document.execCommand('copy')
    } catch (error) {
      console.error('Fallback copy failed:', error)
    } finally {
      textArea.remove()
    }
  }

  showCopyFeedback(button) {
    const originalIcon = button.innerHTML
    
    // Change to check icon
    button.innerHTML = `
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>
    `
    
    // Change color to indicate success
    button.classList.add('text-green-500')
    button.classList.remove('text-slate-400', 'hover:text-slate-200')
    
    // Reset after 2 seconds
    setTimeout(() => {
      button.innerHTML = originalIcon
      button.classList.remove('text-green-500')
      button.classList.add('text-slate-400', 'hover:text-slate-200')
    }, 2000)
  }

  showCopyError(button) {
    const originalIcon = button.innerHTML
    
    // Change to error icon
    button.innerHTML = `
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    `
    
    // Change color to indicate error
    button.classList.add('text-red-500')
    button.classList.remove('text-slate-400', 'hover:text-slate-200')
    
    // Reset after 2 seconds
    setTimeout(() => {
      button.innerHTML = originalIcon
      button.classList.remove('text-red-500')
      button.classList.add('text-slate-400', 'hover:text-slate-200')
    }, 2000)
  }

  // Handle section navigation
  navigateToSection(event) {
    event.preventDefault()
    const sectionId = event.currentTarget.dataset.section
    const section = document.getElementById(sectionId)
    
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' })
      
      // Update URL without triggering page reload
      history.pushState(null, null, `#${sectionId}`)
    }
  }

  // Handle context switching (workspace/site)
  switchContext(event) {
    const contextType = event.currentTarget.dataset.contextType
    const contextId = event.currentTarget.dataset.contextId
    
    // Update the current context in the UI
    this.updateContextDisplay(contextType, contextId)
    
    // Reload examples with new context
    this.reloadExamples(contextType, contextId)
  }

  updateContextDisplay(contextType, contextId) {
    const contextDisplay = this.element.querySelector('[data-context-display]')
    if (contextDisplay) {
      contextDisplay.textContent = `${contextType}: ${contextId}`
    }
    
    // Update any context-dependent examples
    const contextElements = this.element.querySelectorAll('[data-context-dependent]')
    contextElements.forEach(element => {
      element.dataset.currentContext = contextId
      element.dataset.currentContextType = contextType
    })
  }

  async reloadExamples(contextType, contextId) {
    try {
      // Show loading state
      this.showLoadingState()
      
      // Fetch updated examples for the new context
      const response = await fetch(`/developer/examples?${contextType}_id=${contextId}`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        this.updateExamples(data.examples)
      }
    } catch (error) {
      console.error('Failed to reload examples:', error)
    } finally {
      this.hideLoadingState()
    }
  }

  updateExamples(examples) {
    // Update code examples with context-specific data
    const exampleElements = this.element.querySelectorAll('[data-example-type]')
    
    exampleElements.forEach(element => {
      const exampleType = element.dataset.exampleType
      if (examples[exampleType]) {
        const codeBlock = element.querySelector('code')
        if (codeBlock) {
          codeBlock.textContent = examples[exampleType]
        }
      }
    })
  }

  showLoadingState() {
    const loadingElements = this.element.querySelectorAll('[data-loading-target]')
    loadingElements.forEach(element => {
      element.classList.add('animate-pulse', 'opacity-50')
    })
  }

  hideLoadingState() {
    const loadingElements = this.element.querySelectorAll('[data-loading-target]')
    loadingElements.forEach(element => {
      element.classList.remove('animate-pulse', 'opacity-50')
    })
  }

  // Handle API key visibility toggle
  toggleApiKey(event) {
    event.preventDefault()
    const keyElement = event.currentTarget.closest('[data-api-key-container]').querySelector('[data-api-key]')
    const toggleButton = event.currentTarget
    
    if (keyElement.dataset.masked === 'true') {
      // Show full key
      keyElement.textContent = keyElement.dataset.fullKey
      keyElement.dataset.masked = 'false'
      toggleButton.textContent = 'Hide'
    } else {
      // Mask key
      keyElement.textContent = keyElement.dataset.maskedKey
      keyElement.dataset.masked = 'true'
      toggleButton.textContent = 'Show'
    }
  }

  // Handle search functionality
  search(event) {
    const searchTerm = event.target.value.toLowerCase()
    const searchableElements = this.element.querySelectorAll('[data-searchable]')
    
    searchableElements.forEach(element => {
      const content = element.textContent.toLowerCase()
      const isVisible = content.includes(searchTerm) || searchTerm === ''
      
      element.style.display = isVisible ? '' : 'none'
    })
    
    // Update search results count
    const visibleCount = Array.from(searchableElements).filter(el => el.style.display !== 'none').length
    this.updateSearchResults(visibleCount, searchableElements.length)
  }

  updateSearchResults(visibleCount, totalCount) {
    const resultsElement = this.element.querySelector('[data-search-results]')
    if (resultsElement) {
      if (visibleCount === totalCount) {
        resultsElement.textContent = ''
      } else {
        resultsElement.textContent = `Showing ${visibleCount} of ${totalCount} results`
      }
    }
  }

  // Handle section filtering
  filterSections(event) {
    const category = event.currentTarget.dataset.category
    const sections = this.element.querySelectorAll('[data-section-category]')
    
    sections.forEach(section => {
      const sectionCategory = section.dataset.sectionCategory
      const isVisible = category === 'all' || sectionCategory === category
      
      section.style.display = isVisible ? '' : 'none'
    })
    
    // Update active filter button
    this.updateActiveFilter(event.currentTarget)
  }

  updateActiveFilter(activeButton) {
    const filterButtons = this.element.querySelectorAll('[data-filter-button]')
    
    filterButtons.forEach(button => {
      button.classList.remove('bg-blue-600', 'text-white')
      button.classList.add('bg-gray-200', 'text-gray-700')
    })
    
    activeButton.classList.remove('bg-gray-200', 'text-gray-700')
    activeButton.classList.add('bg-blue-600', 'text-white')
  }
}
